import { useMemo } from 'react';
import type { StoreActiveContext } from '@pc/db';
import { MissingActiveContextError } from '@pc/core';

/**
 * Mirrors the @pc/modules-orders pattern: reads active org/store from the
 * web shell's localStorage contract instead of importing from
 * apps/web. Throws MissingActiveContextError so the router-level error
 * element bounces to /choose-context. Returns the narrower
 * `StoreActiveContext` (storeId required) since fulfillment is a
 * per-store module.
 */
export function useActiveCtx(): StoreActiveContext {
  if (typeof window === 'undefined') {
    throw new Error('useActiveCtx requires a browser environment.');
  }
  const orgId = window.localStorage.getItem('pc.activeOrgId');
  const storeId = window.localStorage.getItem('pc.activeStoreId');
  // Memoize so the returned reference is stable per (orgId, storeId)
  // pair. Without this, every render allocates a fresh object and any
  // caller using `ctx` directly as a useEffect dep re-fires the effect
  // every render, looping the underlying RPCs.
  const ctx = useMemo<StoreActiveContext | null>(
    () => (orgId && storeId ? { orgId, storeId } : null),
    [orgId, storeId],
  );
  if (!ctx) {
    throw new MissingActiveContextError({ orgId: !orgId, storeId: !storeId });
  }
  return ctx;
}
